import React, { forwardRef, useState } from 'react';
import { cn } from '@/lib/utils';

/* ─── Types ─────────────────────────────────────────────────── */
export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type AvatarStatus = 'online' | 'offline' | 'busy';

const sizeStyles: Record<AvatarSize, string> = {
  xs: 'h-6 w-6 text-[10px]',
  sm: 'h-8 w-8 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-12 w-12 text-base',
  xl: 'h-16 w-16 text-lg',
};

const statusDotSize: Record<AvatarSize, string> = {
  xs: 'h-1.5 w-1.5 border',
  sm: 'h-2 w-2 border',
  md: 'h-2.5 w-2.5 border-2',
  lg: 'h-3 w-3 border-2',
  xl: 'h-3.5 w-3.5 border-2',
};

const statusColor: Record<AvatarStatus, string> = {
  online: 'bg-green-500',
  offline: 'bg-gray-400',
  busy: 'bg-red-500',
};

/* ─── Avatar ────────────────────────────────────────────────── */
export interface AvatarProps extends React.HTMLAttributes<HTMLDivElement> {
  src?: string | null;
  alt?: string;
  name?: string;
  size?: AvatarSize;
  status?: AvatarStatus;
}

export const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
  ({ className, src, alt, name, size = 'md', status, ...props }, ref) => {
    const [imgError, setImgError] = useState(false);

    const initials = name
      ? name
          .trim()
          .split(/\s+/)
          .map((w) => w[0])
          .slice(0, 2)
          .join('')
          .toUpperCase()
      : '?';

    return (
      <div
        ref={ref}
        className={cn('relative inline-flex shrink-0', className)}
        {...props}
      >
        <div
          className={cn(
            'flex items-center justify-center overflow-hidden rounded-full bg-primary-100 font-semibold text-primary-700',
            'dark:bg-primary-900/40 dark:text-primary-300',
            sizeStyles[size]
          )}
        >
          {src && !imgError ? (
            <img
              src={src}
              alt={alt ?? name ?? 'Avatar'}
              onError={() => setImgError(true)}
              className="h-full w-full object-cover"
            />
          ) : (
            <span aria-hidden="true">{initials}</span>
          )}
        </div>

        {status && (
          <span
            className={cn(
              'absolute bottom-0 end-0 rounded-full border-white dark:border-gray-900',
              statusDotSize[size],
              statusColor[status]
            )}
            aria-label={status}
          />
        )}
      </div>
    );
  }
);

/* ─── AvatarGroup ───────────────────────────────────────────── */
export interface AvatarGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  max?: number;
  size?: AvatarSize;
  children: React.ReactNode;
}

export const AvatarGroup = forwardRef<HTMLDivElement, AvatarGroupProps>(
  ({ className, max, size = 'md', children, ...props }, ref) => {
    const items = React.Children.toArray(children);
    const visible = max ? items.slice(0, max) : items;
    const remaining = max ? items.length - max : 0;

    return (
      <div
        ref={ref}
        className={cn('flex items-center [&>*+*]:-ms-2', className)}
        {...props}
      >
        {visible.map((child, i) => (
          <div key={i} className="ring-2 ring-white dark:ring-gray-900 rounded-full">
            {React.isValidElement<AvatarProps>(child)
              ? React.cloneElement(child, { size })
              : child}
          </div>
        ))}
        {remaining > 0 && (
          <div
            className={cn(
              'flex items-center justify-center rounded-full bg-gray-200 font-semibold text-gray-600 ring-2 ring-white',
              'dark:bg-gray-700 dark:text-gray-300 dark:ring-gray-900',
              sizeStyles[size]
            )}
          >
            +{remaining}
          </div>
        )}
      </div>
    );
  }
);

Avatar.displayName = 'Avatar';
AvatarGroup.displayName = 'AvatarGroup';
